// src/components/practice/tasks/MatchingQuestionsToParagraphs.jsx
import React from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import MatchingQuestion from '../MatchingQuestion';
import { X } from 'lucide-react';

const MatchingQuestionsToParagraphs = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  handleBatchAnswerChange,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  onOpenMobileAnswerSheet,
  ...props
}) => {

  // 1. Map the Questions
  const mqQuestions = questions.map(q => ({
    questionId: q.questionId,
    number: q.questionNumber,
    text: q.questionText,
  }));

  // 2. Define the Options (A, B, C, D, E)
  const paragraphLetters = ['a', 'b', 'c', 'd', 'e'];
  const mqOptions = paragraphLetters.map(id => ({
    id: id,
    text: `Abschnitt ${id.toUpperCase()}`
  }));

  // Add the special "no match" option
  const specialOption = { id: partData.examProvider === 'TELC' ? 'x' : '0' };
  const allOptions = [...mqOptions, specialOption];

  // Parse mainText to extract topic and paragraphs
  const mainTextContent = partData.content?.mainText || partData.content?.text || '';
  const lines = mainTextContent.split('\n');

  // First line is the topic
  const topic = lines[0]?.trim() || '';

  // Parse paragraphs (a), b), c), etc.)
  const paragraphs = [];
  let currentParagraph = null;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();

    // Check if line starts with a letter followed by )
    const paragraphMatch = line.match(/^([a-e])\)\s*(.*)/);

    if (paragraphMatch) {
      // Save previous paragraph if exists
      if (currentParagraph) {
        paragraphs.push(currentParagraph);
      }

      // Start new paragraph
      currentParagraph = {
        letter: paragraphMatch[1],
        text: paragraphMatch[2]
      };
    } else if (currentParagraph && line) {
      // Continue current paragraph
      currentParagraph.text += ' ' + line;
    }
  }

  // Add last paragraph
  if (currentParagraph) {
    paragraphs.push(currentParagraph);
  }

  // --- LEFT COLUMN CONTENT (Reading) ---
  const leftContent = (
    <div className="space-y-6">
      {/* Main Text Content */}
      <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
        {/* Topic Header - Big, Bold, Underlined */}
        {topic && (
          <h2 className="text-2xl font-bold text-gray-900 border-b-4 border-gray-900 pb-4 mb-6">
            {topic}
          </h2>
        )}

        {/* Alternative title if no topic in mainText */}
        {!topic && partData.content?.title && (
          <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
            {partData.content.title}
          </h3>
        )}

        {/* Paragraphs with Bold Letters */}
        <div className="space-y-4">
          {paragraphs.map((para, index) => (
            <div key={para.letter} className="text-justify">
              <p className="text-gray-900 leading-relaxed">
                <span className="font-bold text-lg mr-2">
                  {para.letter})
                </span>
                {para.text}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // --- RIGHT COLUMN CONTENT (Answering) ---
  const rightContent = (
    <div className="space-y-4">
      {/* Answer Sheet Header */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Welche Aussage passt zu welchem Abschnitt?
        </p>
      </div>

      {/* --- Examples List --- */}
      {examples && examples.map(ex => {
        const correctAnswer = ex.correctAnswer;
        const correctOption = allOptions.find(opt => opt.id === correctAnswer);
        const answerText = correctOption ? correctOption.id.toUpperCase() : correctAnswer;

        return (
          <div
            key={ex.questionId}
            className="border-2 border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90"
          >
            <div className="flex items-center justify-between">
              {/* Left side: Example Number and Text */}
              <div className="flex-1 flex items-start">
                <span className="font-bold text-sm mr-2 pt-0.5">
                  {ex.questionNumber || '0'}.
                </span>
                <span className="text-sm leading-relaxed">
                  {ex.questionText}
                </span>
              </div>

              {/* Right side: Disabled Answer */}
              <div className="ml-4 flex-shrink-0">
                <div
                  className="border-2 border-gray-900 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-none cursor-not-allowed"
                  style={{ minWidth: '70px', textAlign: 'center' }}
                >
                  {answerText}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Matching Component */}
      <div className="border-2 border-gray-900 bg-white p-4 rounded-none">
        <MatchingQuestion
          questions={mqQuestions}
          options={allOptions}
          savedAnswers={partAnswers}
          onAnswerChange={handleBatchAnswerChange}
          title="Abschnitt wählen"
          flaggedQuestions={flaggedQuestions}
          toggleFlagQuestion={toggleFlagQuestion}
          exampleAnswers={[]}
          allowMultipleMatches={true}
        />
      </div>
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="grid grid-cols-1 md:grid-cols-5 md:gap-8">

        {/* Left Column (Reading) */}
        <div className="md:col-span-3 min-w-0">
          {leftContent}
          {onOpenMobileAnswerSheet && (
            <button
              type="button"
              onClick={onOpenMobileAnswerSheet}
              className="md:hidden w-full mt-4 py-3 px-4 bg-[#006D77] text-white font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform touch-manipulation"
            >
              <span>Fragen beantworten</span>
              <span aria-hidden>→</span>
            </button>
          )}
        </div>

        {/* Right Column (Answer Sheet) - hidden on mobile */}
        <div className="hidden md:block md:col-span-2 mt-6 md:mt-0">
          <div className="md:sticky md:top-6">
            {rightContent}
          </div>
        </div>

      </div>
      {mobileOverlay}
    </div>
  );
};

export default MatchingQuestionsToParagraphs;